/* =============================================================
   What the bridge knows how to show.

   One entry per entity type. A view never imports another view --
   it calls push('batch', id) and this table decides what that
   means. That is what keeps the graph from becoming forty
   components importing each other.

   To add a type: write a view component, give it a loader, add a
   line here. Nothing else in the app changes.

   ENTRY SHAPE
     label    what the header calls it, singular
     load     (id) => Promise<entity>. Every view loads its own
              record by id rather than trusting what the previous
              screen handed over -- otherwise the same batch looks
              different depending on which door you came through.
     title    (entity) => the heading, once loaded
     tabs     the tab keys this view accepts, first one default.
              Used to validate a tab arriving from a URL.
     View     the component; gets { id, entity, tab, setTab }
   ============================================================= */

import { lazy } from 'react';
import { getStudentByIdFromStudents } from '../../../../services/StudentService';
import { getBatchById } from '../../../../services/BatchService';
import { getTrainerByTrainerId } from '../../../../services/TrainerService';
import { getCourseById } from '../../../../services/CourseFromBilling';
import { getStudentDashboard } from '../../../../services/StudentDashboardService';
import { getEventById } from '../../../../services/EventService';
import { getMockInterviewEventStats } from '../../../../services/MockInterviewListService';
import { getJobById } from '../../../../services/JobsService';
import { getClassroomById } from '../../../../services/ClassroomService';
import { getBranchById } from '../../../../services/BranchService';

import StudentView from './views/StudentView.jsx';
import BatchView from './views/BatchView.jsx';
import TrainerView from './views/TrainerView.jsx';
import CourseView from './views/CourseView.jsx';
import EventView from './views/EventView.jsx';
import JobView from './views/JobView.jsx';
import ClassroomView from './views/ClassroomView.jsx';

/* The services disagree about what they return -- some hand back
   the record, some wrap it in { data }, some wrap that again.
   Unwrapped here, once, so a view can assume it has the thing. */
const unwrap = (res) => res?.data?.data ?? res?.data ?? res ?? null;

export const WAYPOINTS = {
  student: {
    label: 'Student',
    /* Two sources, in parallel. The record carries who they are; the
       dashboard payload carries everything the tabs below are FOR --
       batches, upcoming, courses, attendance. One endpoint would be
       nicer and neither is going to become the other, so they are
       merged here rather than in the view.

       Either may fail on its own without taking the popup down: a
       student with no dashboard data is still a student worth
       reading, and a dashboard without the record still lists the
       batches. Only both failing is a failure. */
    /* The record comes from /students -- the BILLING collection, which is
       what every field below is written against: name and studentIdNumber
       in the heading, and email / phone / courseIds / departmentIds /
       preferredBranchId / modeOfClass / contactPersonIds /
       isVerifiedbyCounsellor on the details tab.

       It used to read /studentprofiles/:id, which is a DIFFERENT
       collection -- profileImage, firstName, address, aadharNo, marks --
       with none of those fields and its own ids. Every caller passes a
       billing student id (the trainer dashboard's rows come straight from
       billingStudentPrisma), so that lookup could only ever 404, the
       catch below swallowed it, and the popup drew a record that was half
       missing rather than saying anything was wrong. */
    load: async (id) => {
      const [record, dash] = await Promise.all([
        getStudentByIdFromStudents(id).then(unwrap).catch(() => null),
        getStudentDashboard({ studentId: id })
          .then((r) => r?.data || null)
          .catch(() => null),
      ]);
      if (!record && !dash) return null;
      return { ...(dash?.student || {}), ...(record || {}), dashboard: dash || null };
    },
    title: (e) => e?.name || 'Unnamed student',
    subtitle: (e) => e?.studentIdNumber || e?.email || null,
    /* These are not different things, they are one student read six
       ways -- which is exactly what a tab is for. Giving "upcoming"
       or "attendance" their own entity type would mean inventing ids
       for things that do not have any. */
    tabs: ['details', 'batches', 'running', 'upcoming', 'courses', 'attendance'],
    View: StudentView,
  },

  batch: {
    label: 'Batch',
    // includeStudents so the Students tab has something to show
    // without a second round trip the moment the tab is clicked.
    load: async (id) => unwrap(await getBatchById(id, { includeStudents: true })),
    title: (e) => e?.name || 'Unnamed batch',
    subtitle: (e) => e?.courseName || e?.course?.name || null,
    tabs: ['details', 'students'],
    /* A student is entitled to their batch; they are not entitled to the
       people in it. The server stops handing the roster to a non-staff
       caller (batches/batch.service.js), and this stops the tab being
       offered at all -- without it a student gets an empty Students tab,
       which advertises a list they may not have and reads as a fault
       rather than a boundary. The host also refuses the tab when it
       arrives from a pasted URL. */
    staffOnlyTabs: ['students'],
    View: BatchView,
    /* An "Edit batch" button in the popup's footer, for a reader who may
       update batches: the batch list's own edit form, over the popup.
       Lazy, so the batch form loads only when someone edits. */
    edit: {
      label: 'Edit batch',
      permission: ['batches', 'update', 'lms'],
      Form: lazy(() => import('./views/BatchEditForm.jsx')),
    },
  },

  trainer: {
    label: 'Trainer',
    load: async (id) => unwrap(await getTrainerByTrainerId(id)),
    // trainerName is what this collection stores; `name` is a
    // convenience some endpoints add and others do not.
    title: (e) => e?.trainerName || e?.name || 'Unnamed trainer',
    subtitle: (e) => e?.trainerEmail || e?.email || null,
    tabs: ['details'],
    View: TrainerView,
  },

  course: {
    label: 'Course',
    load: async (id) => unwrap(await getCourseById(id)),
    title: (e) => e?.name || 'Unnamed course',
    subtitle: (e) => e?.departmentName || e?.department?.name || null,
    tabs: ['details'],
    View: CourseView,
  },

  event: {
    label: 'Event',
    /* The view endpoint attaches trainerId, batchId and venueId as
       populated records rather than ids (see event.joinconfig.js), so
       the two list tabs below have names and ids to drill with and
       need no second request. */
    /* Two sources, in parallel. The record says what the event IS; the
       mock-interview stats say who turned up and how they did -- the
       same GET /mockinterviews/event-stats the /mock-interviews page
       reads, asked here for one event.

       The stats may fail, or the event may not be a mock interview at
       all, without taking the popup down: an event is still worth
       reading without a turnout. Only the record failing is a failure. */
    load: async (id) => {
      const [record, stats] = await Promise.all([
        getEventById(id).then(unwrap).catch(() => null),
        getMockInterviewEventStats([id]).catch(() => ({})),
      ]);
      if (!record) return null;
      return { ...record, mockStats: (stats && stats[String(id)]) || null };
    },
    title: (e) => e?.name || 'Untitled event',
    subtitle: (e) => e?.category || null,
    tabs: ['details', 'trainers', 'batches', 'students'],
    View: EventView,
  },

  job: {
    label: 'Job',
    load: async (id) => unwrap(await getJobById(id)),
    title: (e) => e?.jobName || 'Untitled job',
    subtitle: (e) => e?.organizationName || null,
    tabs: ['details'],
    View: JobView,
  },

  classroom: {
    label: 'Lab',
    /* The record stores its branch as a bare id. The name is one more
       request, made only when the record does not already carry it, and
       a branch that fails to resolve is not a reason to fail the lab --
       the view shows no branch instead. */
    load: async (id) => {
      const record = unwrap(await getClassroomById(id));
      if (!record) return null;
      if (record.branchName || !record.branch) return record;
      const branch = await getBranchById(record.branch).then(unwrap).catch(() => null);
      return { ...record, branchName: branch?.name || branch?.branchName || null };
    },
    title: (e) => e?.classroomNo || 'Unnamed lab',
    subtitle: (e) => [e?.branchName, e?.floor].filter(Boolean).join(' · ') || null,
    tabs: ['details'],
    View: ClassroomView,
  },
};

export const getWaypoint = (type) => WAYPOINTS[type] || null;

/** The tab to use for a frame: the asked-for one if the view has it, else its first. */
export const resolveTab = (type, tab) => {
  const entry = getWaypoint(type);
  if (!entry) return undefined;
  const tabs = entry.tabs || [];
  if (tab && tabs.includes(tab)) return tab;
  return tabs[0];
};
